import { useEffect, useRef, useState } from 'react';
import { Assignment, TimeEntry, getActiveTimeEntry } from './assignment';

/**
 * A hook that provides timer functionality for an assignment
 *
 * This hook handles:
 * 1. Tracking elapsed time for an active assignment timer
 * 2. Updating the timer display every second
 * 3. Resetting the timer when needed
 * 4. Tracking when a timer has been manually stopped vs. automatic updates
 */
export function useTimer(assignment: Assignment | null, userId: string) {
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [isTimerRunning, setIsTimerRunning] = useState<boolean>(false);
  const [timerInterval, setTimerInterval] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [activeTimeEntry, setActiveTimeEntry] = useState<TimeEntry | undefined>(undefined);

  // Track if timer has been manually stopped
  const hasBeenManuallyStoppedRef = useRef<boolean>(false);

  // Store the assignment in a ref to compare with new assignments
  const assignmentRef = useRef<Assignment | null>(null);

  // Find active time entry for this user when assignment changes
  useEffect(() => {
    if (assignment && userId) {
      const entry = getActiveTimeEntry(assignment, userId);
      setActiveTimeEntry(entry);
      setIsTimerRunning(Boolean(entry));

      if (entry?.startTime) {
        const initialElapsed = Date.now() - entry.startTime;
        setElapsedTime(initialElapsed);
        hasBeenManuallyStoppedRef.current = false;
      } else if (!entry) {
        setElapsedTime(0);
      }
    } else {
      setActiveTimeEntry(undefined);
      setIsTimerRunning(false);
      setElapsedTime(0);
    }

    assignmentRef.current = assignment;
  }, [assignment, userId]);

  // Force a refresh of the timer every second when a timer is active
  useEffect(() => {
    let intervalId: ReturnType<typeof setTimeout> | null = null;

    const updateTimer = () => {
      const timeEntry = activeTimeEntry;
      if (timeEntry?.startTime && !hasBeenManuallyStoppedRef.current) {
        const currentElapsed = Date.now() - timeEntry.startTime;
        setElapsedTime(currentElapsed);
      }
    };

    // Only start the timer if we have an active time entry and it hasn't been manually stopped
    if (activeTimeEntry && !hasBeenManuallyStoppedRef.current) {
      // Set initial state
      setIsTimerRunning(true);

      // Update the timer immediately
      updateTimer();

      // Set up the interval for continuous updates
      intervalId = setInterval(updateTimer, 1000);
      setTimerInterval(intervalId as any);
    } else {
      setIsTimerRunning(false);

      // Reset elapsed time if there's no active timer
      if (!activeTimeEntry) {
        setElapsedTime(0);
      }
    }

    // Cleanup function to clear the timer when component unmounts
    // or when the dependencies change
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
        setTimerInterval(null);
      }
    };
  }, [activeTimeEntry]);

  return {
    elapsedTime,
    isTimerRunning,
    timerInterval,
    activeTimeEntry,
    resetTimer: () => {
      if (timerInterval) {
        clearInterval(timerInterval);
        setTimerInterval(null);
      }
      setElapsedTime(0);
      setIsTimerRunning(false);
      setActiveTimeEntry(undefined);
      // Mark that this timer has been manually stopped
      hasBeenManuallyStoppedRef.current = true;
    },
  };
}
